import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarkdownPreview } from '@/presentation/components/MarkdownPreview';
import { MarkdownEditor } from '@/presentation/components/MarkdownEditor';
import { useHelpCategories } from '@/presentation/hooks/useHelpDocumentation';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import {
  GetHelpArticleBundleAction,
  SaveHelpArticleBundleAction,
  TranslateHelpTextAction,
} from '@/application/actions/help/HelpDocumentationActions';
import type {
  HelpArticleBundleDTO,
} from '@/domain/repositories/IHelpDocumentationRepository';

const LOCALES = ['es', 'en', 'pt'] as const;
type LocaleCode = (typeof LOCALES)[number];

type LocaleForm = {
  id: string | null;
  title: string;
  bodyMarkdown: string;
  isPublished: boolean;
};

function emptyLocale(): LocaleForm {
  return { id: null, title: '', bodyMarkdown: '', isPublished: false };
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 150);
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return ax.response?.data?.message || (err instanceof Error ? err.message : fallback);
}

function bundleToLocales(bundle: HelpArticleBundleDTO): Record<LocaleCode, LocaleForm> {
  const next: Record<LocaleCode, LocaleForm> = {
    es: emptyLocale(),
    en: emptyLocale(),
    pt: emptyLocale(),
  };
  for (const locale of LOCALES) {
    const row = bundle.locales[locale];
    if (!row) continue;
    next[locale] = {
      id: row.id,
      title: row.title,
      bodyMarkdown: row.bodyMarkdown,
      isPublished: row.isPublished,
    };
  }
  return next;
}

export const DocumentationArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { categories } = useHelpCategories();

  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [categoryId, setCategoryId] = useState(() =>
    isNew ? searchParams.get('categoryId') ?? '' : ''
  );
  const [sortOrder, setSortOrder] = useState(0);
  const [locales, setLocales] = useState<Record<LocaleCode, LocaleForm>>({
    es: emptyLocale(),
    en: emptyLocale(),
    pt: emptyLocale(),
  });
  const [activeBodyLocale, setActiveBodyLocale] = useState<LocaleCode>('es');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const action = container.get<GetHelpArticleBundleAction>(TYPES.GetHelpArticleBundleAction);
        const bundle = await action.byId(id!);
        if (cancelled) return;
        setSlug(bundle.slug);
        setSlugTouched(true);
        setCategoryId(bundle.categoryId ?? '');
        setSortOrder(bundle.sortOrder);
        setLocales(bundleToLocales(bundle));
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, t('pages.documentation.saveError')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  const updateLocale = (locale: LocaleCode, patch: Partial<LocaleForm>) => {
    setLocales((prev) => ({ ...prev, [locale]: { ...prev[locale], ...patch } }));
  };

  const handleEsTitleChange = (value: string) => {
    updateLocale('es', { title: value });
    if (!slugTouched) setSlug(slugify(value));
  };

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      const esTitle = locales.es.title.trim();
      if (!esTitle) {
        setToast(t('pages.documentation.titleRequiredEs', {
          defaultValue: 'Completá el título en español para guardar',
        }));
        return;
      }
      if (!categoryId) {
        setToast(t('pages.documentation.categoryRequired', {
          defaultValue: 'Elegí una categoría/sección para el documento',
        }));
        return;
      }

      const action = container.get<SaveHelpArticleBundleAction>(TYPES.SaveHelpArticleBundleAction);
      const finalSlug = slug.trim() || slugify(esTitle) || 'articulo';
      const bundle = await action.execute({
        slug: finalSlug,
        contentType: 'article',
        categoryId,
        sortOrder: Number(sortOrder) || 0,
        tag: null,
        locales: LOCALES.map((locale) => ({
          locale,
          id: locales[locale].id,
          title: locales[locale].title,
          bodyMarkdown: locales[locale].bodyMarkdown,
          isPublished: locales[locale].isPublished,
        })),
      });
      if (!bundle) {
        setToast(t('pages.documentation.saveError'));
        return;
      }
      setLocales(bundleToLocales(bundle));
      setSlug(bundle.slug);
      setSlugTouched(true);
      setToast(t('pages.documentation.saved'));
      const primaryId =
        bundle.locales.es?.id || bundle.locales.en?.id || bundle.locales.pt?.id;
      if (isNew && primaryId) {
        navigate(`/documentation/articles/${primaryId}`, { replace: true });
      }
    } catch (err) {
      setToast(apiErrorMessage(err, t('pages.documentation.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoTranslate = async () => {
    const sourceTitle = locales.es.title.trim();
    const sourceBody = locales.es.bodyMarkdown.trim();
    if (!sourceTitle && !sourceBody) {
      setToast(t('pages.documentation.translateNeedEs'));
      return;
    }

    setTranslating(true);
    setToast(null);
    try {
      const action = container.get<TranslateHelpTextAction>(TYPES.TranslateHelpTextAction);
      const targets: LocaleCode[] = ['en', 'pt'];
      const next = { ...locales };

      for (const target of targets) {
        if (sourceTitle) {
          next[target] = {
            ...next[target],
            title: await action.execute(sourceTitle, 'es', target),
          };
        }
        if (sourceBody) {
          next[target] = {
            ...next[target],
            bodyMarkdown: await action.execute(sourceBody, 'es', target),
          };
        }
        // No publicar automático: siempre borrador
        next[target] = { ...next[target], isPublished: false };
      }

      setLocales(next);
      setToast(t('pages.documentation.translateDone'));
      setActiveBodyLocale('en');
    } catch (err) {
      setToast(apiErrorMessage(err, t('pages.documentation.saveError')));
    } finally {
      setTranslating(false);
    }
  };

  const localeLabels = useMemo(
    () => ({
      es: t('pages.documentation.lang.es'),
      en: t('pages.documentation.lang.en'),
      pt: t('pages.documentation.lang.pt'),
    }),
    [t]
  );

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">{t('pages.documentation.loading')}</div>;
  }

  if (error) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <Link to="/documentation" className="text-sm text-teal-600 hover:underline">
          ← {t('pages.documentation.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="panel-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Link to="/documentation" className="text-sm text-teal-600 hover:underline">
            ← {t('pages.documentation.back')}
          </Link>
          <h1 className="panel-page-title mt-1">
            {isNew ? t('pages.documentation.newArticle') : t('pages.documentation.editArticle')}
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            {t('pages.documentation.multiLangHint')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={translating || saving}
            onClick={() => void handleAutoTranslate()}
            className="px-3 py-2 text-sm rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
          >
            {translating
              ? t('pages.documentation.translating')
              : t('pages.documentation.autoTranslate')}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="btn btn-primary"
          >
            {saving ? t('pages.documentation.saving') : t('pages.documentation.save')}
          </button>
        </div>
      </div>

      {toast && (
        <div className="rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 px-4 py-2 text-sm">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <label className="text-xs text-gray-500">
          Slug
          <input
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
          />
        </label>
        <label className="text-xs text-gray-500">
          {t('pages.documentation.columns.category')}
          <select
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            required
          >
            <option value="">{t('pages.documentation.selectCategory', { defaultValue: 'Elegí una sección' })}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.locales.find((l) => l.locale === 'es')?.name ?? c.slug}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-500">
          {t('pages.documentation.columns.sort')}
          <input
            type="number"
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </label>
      </div>

      {/* Títulos + publicar por idioma (siempre visibles) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {LOCALES.map((locale) => (
          <div
            key={locale}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {localeLabels[locale]}
              </p>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={locales[locale].isPublished}
                  onChange={(e) => updateLocale(locale, { isPublished: e.target.checked })}
                />
                {t('pages.documentation.columns.published')}
              </label>
            </div>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={locales[locale].title}
              onChange={(e) =>
                locale === 'es'
                  ? handleEsTitleChange(e.target.value)
                  : updateLocale(locale, { title: e.target.value })
              }
              placeholder={t('pages.documentation.titlePlaceholder', { lang: locale.toUpperCase() })}
            />
          </div>
        ))}
      </div>

      {/* Cuerpo: un editor a la vez + preview, tabs de idioma */}
      <div className="flex gap-2 flex-wrap">
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => setActiveBodyLocale(locale)}
            className={`px-3 py-1.5 text-sm rounded-md border ${
              activeBodyLocale === locale
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
            }`}
          >
            {t('pages.documentation.bodyTab', { lang: locale.toUpperCase() })}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[28rem]">
        <div className="flex flex-col">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Markdown ({activeBodyLocale.toUpperCase()})
          </p>
          <MarkdownEditor
            value={locales[activeBodyLocale].bodyMarkdown}
            onChange={(value) => updateLocale(activeBodyLocale, { bodyMarkdown: value })}
            placeholder={t('pages.documentation.editorPlaceholder')}
          />
        </div>
        <div className="flex flex-col">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t('pages.documentation.preview')} ({activeBodyLocale.toUpperCase()})
          </p>
          <div className="flex-1 min-h-[24rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 overflow-auto">
            <MarkdownPreview content={locales[activeBodyLocale].bodyMarkdown} />
          </div>
        </div>
      </div>
    </div>
  );
};
